import {
  fetchProvenanceFileInfo,
  fetchProvenanceTreePage,
  type HierarchyItem,
  type ProvenanceFileInfoBody,
  type TreeNodeResponse,
} from '@/shared/cors-provenance-api';
import { fetchJunkdrawerMboxEmail } from '@/shared/cors-junkdrawer-api';
import {
  fileInfoFromProvenanceBody,
  getDispatchedFilePath,
  getMediaType,
  isArchiveMemberSearchablePath,
  resolveFileInfoMediaDisplayUrl,
  toProvenanceLookupPath,
} from '@/shared/functions';
import type { FileSummary, MboxAttachment, MediaSource, Source } from '@/shared/types';

const ATTACHMENT_EXTENSIONS_PREFER_PREVIEW = new Set(['heic', 'heif', 'tif', 'tiff']);

function extensionFromUrl(url: string): string {
  const path = pathWithoutQuery(url) ?? url;
  const fileName = path.split('/').pop() ?? path;
  const dot = fileName.lastIndexOf('.');
  if (dot <= 0 || dot === fileName.length - 1) return '';
  return fileName.slice(dot + 1).toLowerCase();
}

export type EmailUrlCarrier = {
  originalUrl?: string;
  sourceFilePath?: string;
  sourceUrl?: string;
  mediaUrl?: string;
  mediaSrc?: string;
  srcToUse?: string;
  previewFilePath?: string;
  analysisFilePath?: string;
  dispatchedFilePath?: string;
  sourceContext?: Record<string, unknown>;
  mediaContext?: Record<string, unknown>;
};

export type EmailFetchArgs =
  | {
      kind: 'mbox';
      containerUrl: string;
      previewUrl?: string;
      emailIndex: number | null;
    }
  | {
      kind: 'eml';
      emlUrl: string;
    };

export type EmailCitationPayload = {
  emailUrl: string | null;
  attachments: MboxAttachment[];
  fileInfo?: FileSummary | null;
  hierarchy?: HierarchyItem[] | null;
};

function pathWithoutQuery(url: string): string {
  return url.trim().split(/[?#]/)[0];
}

function attachmentStemFromUrl(url?: string | null): { dir: string; stem: string } | null {
  if (!url?.trim()) return null;
  const normalized = pathWithoutQuery(url).trim();
  if (!normalized) return null;
  const parts = normalized.split('/');
  const fileName = parts.pop() ?? '';
  const dot = fileName.lastIndexOf('.');
  const stem = dot > 0 ? fileName.slice(0, dot).toLowerCase() : fileName.toLowerCase();
  if (!stem) return null;
  return {
    dir: parts.join('/').toLowerCase(),
    stem,
  };
}

export function urlsLikelySameAttachment(
  left?: string | null,
  right?: string | null,
): boolean {
  const leftTrimmed = left?.trim();
  const rightTrimmed = right?.trim();
  if (!leftTrimmed || !rightTrimmed) return false;
  if (leftTrimmed === rightTrimmed) return true;
  const leftStem = attachmentStemFromUrl(leftTrimmed);
  const rightStem = attachmentStemFromUrl(rightTrimmed);
  if (!leftStem || !rightStem) return false;
  return leftStem.dir === rightStem.dir && leftStem.stem === rightStem.stem;
}

export function urlLooksLikeMbox(url?: string | null): boolean {
  if (!url?.trim()) return false;
  const path = pathWithoutQuery(url).toLowerCase();
  return path.endsWith('.mbox') || path.endsWith('.mbx');
}

export function urlLooksLikeEml(url?: string | null): boolean {
  if (!url?.trim()) return false;
  return pathWithoutQuery(url).toLowerCase().endsWith('.eml');
}

/**
 * Junkdrawer sometimes returns ``email.json`` / ``email_N.json`` as the email
 * body URL — not displayable text. Do NOT treat every ``.json`` path as junk:
 * real JSON attachments must stay in the attachment list and can be cited.
 */
export function isMboxEmailJsonUrl(url: string | null | undefined): boolean {
  if (!url?.trim()) return false;
  const pathOnly = pathWithoutQuery(url).toLowerCase();
  const fileName = pathOnly.split('/').pop() ?? pathOnly;
  return fileName === 'email.json' || /^email_\d+\.json$/.test(fileName);
}

export function parseEmailIndex(
  context: Record<string, unknown> | undefined,
): number | null {
  if (!context) return null;
  const raw = context.email_index ?? context.emailIndex;
  if (raw === undefined || raw === null || raw === '') return null;
  const parsed = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function collectCandidateUrls(item: EmailUrlCarrier): string[] {
  const fromContext = [
    item.sourceContext?.dispatchedFilePath,
    item.sourceContext?.dispatched_file_path,
    item.sourceContext?.attachmentUrl,
    item.sourceContext?.previewUrl,
    item.mediaContext?.dispatchedFilePath,
    item.mediaContext?.dispatched_file_path,
    item.mediaContext?.previewUrl,
    item.mediaContext?.attachmentUrl,
  ];
  const dispatched = getDispatchedFilePath(item as Source);
  return [
    item.originalUrl,
    item.sourceFilePath,
    item.sourceUrl,
    item.mediaUrl,
    item.mediaSrc,
    item.srcToUse,
    item.previewFilePath,
    item.analysisFilePath,
    item.dispatchedFilePath,
    dispatched,
    ...fromContext,
  ].filter((url): url is string => typeof url === 'string' && url.trim() !== '');
}

/**
 * Prefer a cited/display ``.eml`` (loaded via provenance file-info). Only fall
 * back to the junkdrawer ``/mbox`` API when the source is a mailbox container
 * with no ``.eml`` extract.
 */
export function resolveEmailFetchArgs(
  item: EmailUrlCarrier | null | undefined,
): EmailFetchArgs | null {
  if (!item) return null;
  const urls = collectCandidateUrls(item);
  const emlUrl = urls.find(urlLooksLikeEml);
  if (emlUrl) {
    return { kind: 'eml', emlUrl };
  }
  const mboxUrl = urls.find(urlLooksLikeMbox);
  const emailIndex =
    parseEmailIndex(item.mediaContext) ?? parseEmailIndex(item.sourceContext);
  if (mboxUrl) {
    return {
      kind: 'mbox',
      containerUrl: mboxUrl,
      previewUrl: item.previewFilePath || (item.mediaContext?.previewUrl as string | undefined),
      emailIndex,
    };
  }
  return null;
}

export function isEmlEmailCitation(
  item: EmailUrlCarrier | null | undefined,
): boolean {
  return resolveEmailFetchArgs(item)?.kind === 'eml';
}

export function emailFetchCacheKey(
  args: EmailFetchArgs | null | undefined,
): string {
  if (!args) return '';
  if (args.kind === 'eml') return `eml|${args.emlUrl}`;
  return `mbox|${args.containerUrl}|${args.previewUrl ?? ''}|${args.emailIndex ?? ''}`;
}

function parseEmailIndexFromPreviewUrl(emailUrl: string | undefined): number | null {
  if (!emailUrl) return null;
  const parsedEmailIndex = emailUrl.split('/').pop()?.split('.')[0]?.split('_')[1];
  if (!parsedEmailIndex || Number.isNaN(parseInt(parsedEmailIndex, 10))) return null;
  const parsed = parseInt(parsedEmailIndex, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

/** Id to pass as ``GET /tree?fileStatusId=`` so we list *this* email's children. */
export function treeDrillIdFromFileInfoBody(
  body: ProvenanceFileInfoBody | null | undefined,
): string | null {
  if (!body) return null;
  if (body.kind === 'file') {
    const id = body.file.fileStatusId || body.file.id;
    return typeof id === 'string' && id.trim() ? id.trim() : null;
  }
  if (body.kind === 'subdocument') {
    // Use the eml's own id, not ``rootId`` (the parent zip/mbox).
    const id = body.subdocument.id;
    return typeof id === 'string' && id.trim() ? id.trim() : null;
  }
  return null;
}

export function emailBodyUrlFromFileInfo(
  fileInfo: FileSummary | null | undefined,
  fallbackEmlUrl: string,
): string {
  const fromInfo = resolveFileInfoMediaDisplayUrl(fileInfo, fallbackEmlUrl).trim();
  const candidate = fromInfo || fallbackEmlUrl.trim();
  if (candidate && !isMboxEmailJsonUrl(candidate) && !isArchiveMemberSearchablePath(candidate)) {
    return candidate;
  }
  return fallbackEmlUrl.trim();
}

function attachmentFromTreeNode(
  node: TreeNodeResponse,
  index: number,
): MboxAttachment | null {
  if (node.nodeType === 'directory') return null;
  const info = fileInfoFromProvenanceBody(node.fileInfo);
  const fileUrl = typeof info?.fileUrl === 'string' ? info.fileUrl.trim() : '';
  const displayUrl = resolveFileInfoMediaDisplayUrl(info, '').trim();
  const safeFileUrl =
    fileUrl && !isArchiveMemberSearchablePath(fileUrl) ? fileUrl : '';
  // Keep canonical path matching where possible, but use a preview/transcode
  // when the raw file URL is not browser-renderable by itself (e.g. HEIC).
  const shouldPreferDisplayUrl =
    !!displayUrl &&
    !!safeFileUrl &&
    displayUrl !== safeFileUrl &&
    (ATTACHMENT_EXTENSIONS_PREFER_PREVIEW.has(extensionFromUrl(safeFileUrl)) ||
      getMediaType(
        { mediaType: 'unknown', mediaUrl: safeFileUrl, fileInfo: info ?? undefined } as MediaSource,
        info,
      ) === 'unknown');
  const url = (shouldPreferDisplayUrl ? displayUrl : safeFileUrl || displayUrl).trim();
  if (!url || isArchiveMemberSearchablePath(url)) return null;
  return {
    attachmentIndex: index,
    attachmentUrl: url,
    fileName: info?.fileName || node.name,
    contentType: info?.mimeType ?? null,
    contentLength: info?.size ?? 0,
  };
}

async function fetchAttachmentsFromTree(args: {
  cellId: string;
  accessToken: string;
  fileStatusId: string;
  path?: string;
  excludeUrls: Set<string>;
  signal?: AbortSignal;
  depth?: number;
}): Promise<MboxAttachment[]> {
  const depth = args.depth ?? 0;
  const attachments: MboxAttachment[] = [];
  const directories: TreeNodeResponse[] = [];
  let nextOffset: number | null = 0;
  while (nextOffset !== null && attachments.length < 200) {
    const page = await fetchProvenanceTreePage({
      cellId: args.cellId,
      accessToken: args.accessToken,
      fileStatusId: args.fileStatusId,
      ...(args.path ? { path: args.path } : {}),
      view: 'user',
      offset: nextOffset,
      limit: 200,
      signal: args.signal,
    });
    for (const node of page.items) {
      if (node.nodeType === 'directory' && node.hasChildren && depth < 1) {
        directories.push(node);
        continue;
      }
      const attachment = attachmentFromTreeNode(node, attachments.length);
      if (!attachment) continue;
      if (args.excludeUrls.has(attachment.attachmentUrl.trim())) continue;
      if (isMboxEmailJsonUrl(attachment.attachmentUrl)) continue;
      attachments.push(attachment);
    }
    nextOffset = page.nextOffset;
  }
  for (const dir of directories) {
    if (attachments.length >= 200) break;
    const nested = await fetchAttachmentsFromTree({
      ...args,
      path: dir.path,
      depth: depth + 1,
    });
    for (const attachment of nested) {
      if (attachments.length >= 200) break;
      if (attachments.some((existing) => existing.attachmentUrl === attachment.attachmentUrl)) {
        continue;
      }
      attachments.push({ ...attachment, attachmentIndex: attachments.length });
    }
  }
  return attachments;
}

async function fetchEmlCitationFromFileInfo(
  emlUrl: string,
  session: { cellId: string | null; accessToken: string | null },
  signal?: AbortSignal,
): Promise<EmailCitationPayload | null> {
  if (!emlUrl || !session.cellId || !session.accessToken) {
    return null;
  }
  if (isArchiveMemberSearchablePath(emlUrl)) {
    return { emailUrl: emlUrl, attachments: [], fileInfo: null };
  }
  try {
    const body = await fetchProvenanceFileInfo({
      cellId: session.cellId,
      accessToken: session.accessToken,
      s3Url: toProvenanceLookupPath(emlUrl),
      signal,
    });
    const fileInfo = fileInfoFromProvenanceBody(body);
    const emailUrl = emailBodyUrlFromFileInfo(fileInfo, emlUrl);
    const drillId = treeDrillIdFromFileInfoBody(body);
    const excludeUrls = new Set(
      [emlUrl, emailUrl, fileInfo?.fileUrl, fileInfo?.previewUrl]
        .filter((url): url is string => typeof url === 'string' && url.trim() !== '')
        .map((url) => url.trim()),
    );
    let attachments: MboxAttachment[] = [];
    if (drillId) {
      try {
        attachments = await fetchAttachmentsFromTree({
          cellId: session.cellId,
          accessToken: session.accessToken,
          fileStatusId: drillId,
          excludeUrls,
          signal,
        });
      } catch {
        if (signal?.aborted) return null;
        attachments = [];
      }
    }
    if (signal?.aborted) return null;
    return { emailUrl, attachments, fileInfo, hierarchy: body.hierarchy ?? null };
  } catch {
    if (signal?.aborted) return null;
    return { emailUrl: emlUrl, attachments: [], fileInfo: null, hierarchy: null };
  }
}

async function fetchMboxCitation(
  args: Extract<EmailFetchArgs, { kind: 'mbox' }>,
  session: { cellId: string | null; accessToken: string | null },
  signal?: AbortSignal,
): Promise<EmailCitationPayload | null> {
  try {
    if (!args.containerUrl || !session.cellId || !session.accessToken) {
      return null;
    }
    let emailIndex: number | null = null;
    if (args.emailIndex !== null && args.emailIndex !== undefined && Number.isFinite(args.emailIndex)) {
      emailIndex = args.emailIndex;
    } else {
      emailIndex = parseEmailIndexFromPreviewUrl(args.previewUrl);
    }
    if (emailIndex === null) {
      // Do not default to message 0 — that loads the wrong email.
      return null;
    }
    const data = await fetchJunkdrawerMboxEmail<Partial<{
      emailUrl?: string;
      attachments?: MboxAttachment[];
    }>>({
      cellId: session.cellId,
      accessToken: session.accessToken,
      s3Url: args.containerUrl,
      emailIndex,
      signal,
    });
    if (!data) return null;
    const emailUrl = data.emailUrl?.trim() || '';
    return {
      emailUrl: emailUrl && !isMboxEmailJsonUrl(emailUrl) ? emailUrl : null,
      attachments: Array.isArray(data.attachments) ? data.attachments : [],
    };
  } catch {
    if (signal?.aborted) return null;
    return null;
  }
}

export async function fetchEmailCitation(
  args: EmailFetchArgs,
  session: { cellId: string | null; accessToken: string | null },
  signal?: AbortSignal,
): Promise<EmailCitationPayload | null> {
  if (args.kind === 'eml') {
    return fetchEmlCitationFromFileInfo(args.emlUrl, session, signal);
  }
  return fetchMboxCitation(args, session, signal);
}

/** MediaDisplay item for the email body (plain-text / markdown viewer). */
export function emailBodyMediaItem(
  base: MediaSource | undefined,
  emailUrl: string,
  fileInfo?: FileSummary | null,
): MediaSource {
  const derivedType = getMediaType(
    { mediaUrl: emailUrl, mediaType: 'unknown', fileInfo: fileInfo ?? undefined } as MediaSource,
    fileInfo,
  );
  return {
    ...(base ?? {}),
    mediaType: derivedType === 'unknown' ? 'text' : derivedType,
    mediaUrl: emailUrl,
    mediaSrc: emailUrl,
    srcToUse: emailUrl,
    previewFilePath: emailUrl,
    originalUrl: emailUrl,
    ...(fileInfo ? { fileInfo } : {}),
  } as MediaSource;
}
